import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../components/layout/AppShell';
import { ArrowRight, Activity, Stethoscope } from 'lucide-react';
import { useChallenge } from '../../features/cases/hooks/use-challenges';

const isInvalidPlaceholder = (val) => {
  if (!val) return true;
  const lower = String(val).toLowerCase();
  return lower.includes('clinical essentials') || lower.includes('vet record') || lower.includes('bva member');
};

const CaseIntakePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, isLoading, isError } = useChallenge(id);

  if (isLoading) {
    return (
      <AppShell>
        <div className="flex min-h-screen justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-indigo"></div>
        </div>
      </AppShell>
    );
  }

  if (isError || !data?.data) {
    return (
      <AppShell>
        <div className="flex min-h-screen justify-center items-center text-gray-500">
          Challenge not found.
        </div>
      </AppShell>
    );
  }

  const ch = data.data;
  const signs = ch.clinicalSigns || [];
  const vitals = ch.vitals || {
    temperature: ch.temperature,
    heartRate: ch.heartRate,
    respRate: ch.respRate,
    weight: ch.weight,
  };

  const cleanSpecies = isInvalidPlaceholder(ch.species) ? null : ch.species;
  const cleanPatientInfo = isInvalidPlaceholder(ch.patientInfo) ? null : ch.patientInfo;

  const patientParts = [
    cleanSpecies,
    ch.breed,
    ch.ageValue ? `${ch.ageValue} ${ch.ageUnit || 'years'}` : null,
    ch.sex,
    ch.sterilization,
  ].filter(Boolean);

  const formattedPatient = patientParts.length > 0
    ? patientParts.join(', ')
    : cleanPatientInfo;

  const isDuplicateHistory = ch.history && (
    ch.history.trim() === ch.title?.trim() ||
    ch.history.trim() === ch.chiefComplaint?.trim()
  );
  const showHistory = ch.history && !isDuplicateHistory;

  const hasAnyVitals = vitals.temperature || vitals.heartRate || vitals.respRate || vitals.weight || vitals.bcs || vitals.pain || vitals.crt || vitals.mm;

  return (
    <AppShell>
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto py-6 px-4 flex justify-between items-center">
          <div>
            <button
              onClick={() => navigate('/challenges')}
              className="text-gray-500 hover:text-gray-900 flex items-center text-sm font-medium mb-3 transition-colors"
            >
              <span className="mr-1">{"<"}</span> Back to Cases
            </button>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Case Intake</h1>
            <p className="text-gray-500 text-sm">Review the presenting case details carefully</p>
          </div>
          {cleanSpecies && (
            <span className="px-4 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold border border-blue-200 shadow-sm">
              {cleanSpecies}
            </span>
          )}
        </div>
      </div>

      <div className="bg-gray-50/50 min-h-screen">
        <div className="max-w-4xl mx-auto py-8 px-4 space-y-6 pb-20">

          {/* Title & Patient Info Card (Step 1) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-3">
            <h2 className="text-xl font-bold text-gray-900">{ch.title}</h2>
            {formattedPatient && (
              <div className="flex items-center gap-2 pt-2 border-t border-gray-100 text-sm text-gray-700">
                <span className="font-semibold text-gray-900">Patient Info:</span>
                <span>{formattedPatient}</span>
              </div>
            )}
          </div>

          {/* Chief Complaint & History (Step 2) */}
          {(ch.chiefComplaint || showHistory) && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
              <div className="text-gray-900 font-semibold text-base border-b border-gray-100 pb-3">
                <span>Chief Complaint</span>
              </div>

              {ch.chiefComplaint && (
                <div>
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Chief Complaint</div>
                  <p className="text-sm text-gray-800 bg-gray-50/80 p-3 rounded-lg border border-gray-100 leading-relaxed">
                    {ch.chiefComplaint}
                  </p>
                </div>
              )}

              {showHistory && (
                <div>
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">History</div>
                  <p className="text-sm text-gray-800 bg-gray-50/80 p-3 rounded-lg border border-gray-100 leading-relaxed whitespace-pre-wrap">
                    {ch.history}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Clinical Signs & Symptoms */}
          {signs.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-3">
              <div className="flex items-center gap-2.5 text-amber-700 font-semibold text-base border-b border-gray-100 pb-3">
                <Stethoscope size={18} className="text-amber-600" />
                <span>Clinical Signs & Symptoms</span>
              </div>
              <ul className="list-disc pl-5 space-y-2 text-gray-700 text-sm">
                {signs.map((s, idx) => <li key={idx}>{s}</li>)}
              </ul>
            </div>
          )}

          {/* Vitals (Step 3) */}
          {hasAnyVitals && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
              <div className="flex items-center gap-2.5 text-emerald-700 font-semibold text-base border-b border-gray-100 pb-3">
                <Activity size={18} className="text-emerald-600" />
                <span>Vitals</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {vitals.temperature && (
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="text-gray-500 text-xs font-medium">Temp (°F)</div>
                    <div className="font-semibold text-gray-900 text-sm mt-0.5">{vitals.temperature}</div>
                  </div>
                )}
                {vitals.heartRate && (
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="text-gray-500 text-xs font-medium">HR (/min)</div>
                    <div className="font-semibold text-gray-900 text-sm mt-0.5">{vitals.heartRate}</div>
                  </div>
                )}
                {vitals.respRate && (
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="text-gray-500 text-xs font-medium">RR (/min)</div>
                    <div className="font-semibold text-gray-900 text-sm mt-0.5">{vitals.respRate}</div>
                  </div>
                )}
                {vitals.weight && (
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="text-gray-500 text-xs font-medium">Wt (kg)</div>
                    <div className="font-semibold text-gray-900 text-sm mt-0.5">{vitals.weight}</div>
                  </div>
                )}
                {vitals.bcs && (
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="text-gray-500 text-xs font-medium">BCS</div>
                    <div className="font-semibold text-gray-900 text-sm mt-0.5">{vitals.bcs}</div>
                  </div>
                )}
                {vitals.pain && (
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="text-gray-500 text-xs font-medium">Pain</div>
                    <div className="font-semibold text-gray-900 text-sm mt-0.5">{vitals.pain}</div>
                  </div>
                )}
                {vitals.crt && (
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="text-gray-500 text-xs font-medium">CRT(s)</div>
                    <div className="font-semibold text-gray-900 text-sm mt-0.5">{vitals.crt}</div>
                  </div>
                )}
                {vitals.mm && (
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="text-gray-500 text-xs font-medium">MM color</div>
                    <div className="font-semibold text-gray-900 text-sm mt-0.5">{vitals.mm}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Bottom CTA */}
          <div className="bg-[#7367f0] text-white p-6 md:p-8 rounded-xl mt-6 shadow-sm">
            <div className="mb-6">
              <h3 className="text-xl font-bold mb-1">Ready to diagnose?</h3>
              <p className="text-indigo-100 text-sm">You've reviewed the case details and vitals. Now it's time to submit your clinical approach.</p>
            </div>
            <button
              onClick={() => navigate(`/challenges/${id}/response`)}
              className="bg-white text-[#7367f0] px-6 py-2.5 rounded-lg font-semibold flex items-center hover:bg-gray-50 transition-colors shrink-0 text-sm shadow-sm"
            >
              Continue to Response <ArrowRight size={16} className="ml-2 text-[#7367f0]" />
            </button>
          </div>
        </div>
      </div>
    </AppShell>
  );
};

export default CaseIntakePage;
